/**
 * followups.js — sugerencias de seguimiento contextuales para el chat de ARIA.
 *
 * Tras cada respuesta, proponemos 2-3 "siguientes pasos" según el dominio que
 * el supervisor enrutó (lo emite el backend en `routing`/`_agent`, capturado en
 * AIAssistant). Sin coste ni latencia (no hay llamada extra al LLM): es un mapa
 * curado por dominio. Cada follow-up es { label (chip), prompt (lo que se envía
 * al pulsarlo) }.
 *
 * Los `prompt` están redactados como preguntas/acciones que ARIA ya sabe
 * resolver con sus tools (consultas read + algún write con two-step).
 */

const FOLLOWUPS = {
  invoicing: [
    { label: 'Facturas vencidas', prompt: '¿Qué facturas tengo vencidas?' },
    { label: 'Cobros de esta semana', prompt: '¿Qué cobros tengo esta semana?' },
    { label: 'Clientes que más deben', prompt: '¿Qué clientes me deben más dinero?' },
  ],
  rectification: [
    { label: 'Facturas vencidas', prompt: '¿Qué facturas tengo vencidas?' },
    { label: 'Crear rectificativa', prompt: 'Quiero crear una factura rectificativa' },
  ],
  expenses: [
    { label: 'Gasto del mes', prompt: '¿Cuánto he gastado este mes?' },
    { label: 'Gastos por categoría', prompt: 'Desglósame los gastos por categoría' },
    { label: 'Registrar un gasto', prompt: 'Quiero registrar un gasto' },
  ],
  fiscal: [
    { label: 'Modelo 303 del trimestre', prompt: '¿Cómo va el modelo 303 de este trimestre?' },
    { label: 'IRPF a ingresar', prompt: '¿Cuánto IRPF tengo que ingresar este trimestre?' },
    { label: 'Próximos vencimientos AEAT', prompt: '¿Qué vencimientos fiscales tengo próximamente?' },
  ],
  hr: [
    { label: 'Coste de personal', prompt: '¿Cuál es el coste total de personal este mes?' },
    { label: 'Ausencias del mes', prompt: '¿Qué ausencias hay este mes?' },
    { label: 'Calcular una nómina', prompt: 'Quiero calcular una nómina' },
  ],
  finance: [
    { label: 'Cuadre del mes', prompt: '¿Está cuadrada la contabilidad de este mes?' },
    { label: 'Libro mayor de una cuenta', prompt: 'Enséñame el libro mayor de una cuenta' },
    { label: 'Crear un asiento', prompt: 'Quiero crear un asiento manual' },
  ],
  accounting: [
    { label: 'Cuadre del mes', prompt: '¿Está cuadrada la contabilidad de este mes?' },
    { label: 'Conciliar movimientos', prompt: 'Ayúdame a conciliar los movimientos bancarios' },
    { label: 'Crear un asiento', prompt: 'Quiero crear un asiento manual' },
  ],
  banking: [
    { label: 'Saldo de cuentas', prompt: '¿Cuál es el saldo de mis cuentas?' },
    { label: 'Movimientos sin conciliar', prompt: '¿Qué movimientos bancarios están sin conciliar?' },
    { label: 'Previsión de tesorería', prompt: '¿Cómo está mi previsión de tesorería?' },
  ],
  analytics: [
    { label: 'KPIs del mes', prompt: 'Dame los KPIs de este mes' },
    { label: 'Previsión de caja 60 días', prompt: '¿Tendré problemas de caja en los próximos 60 días?' },
    { label: 'Rentabilidad', prompt: '¿Soy rentable este mes?' },
  ],
  crm: [
    { label: 'Pipeline de ventas', prompt: '¿Cómo va el pipeline de ventas?' },
    { label: 'Oportunidades por cerrar', prompt: '¿Qué oportunidades están por cerrar?' },
    { label: 'Clientes top', prompt: '¿Cuáles son mis mejores clientes?' },
  ],
  projects: [
    { label: 'Estado de proyectos', prompt: '¿Cómo van mis proyectos?' },
    { label: 'Rentabilidad de proyectos', prompt: '¿Qué proyectos son más rentables?' },
    { label: 'Tareas pendientes', prompt: '¿Qué tareas tengo pendientes?' },
  ],
  inventory: [
    { label: 'Stock bajo', prompt: '¿Qué productos tienen stock bajo?' },
    { label: 'Ajustar stock', prompt: 'Quiero ajustar el stock de un producto' },
    { label: 'Crear producto', prompt: 'Quiero crear un producto' },
  ],
  products: [
    { label: 'Stock bajo', prompt: '¿Qué productos tienen stock bajo?' },
    { label: 'Crear producto', prompt: 'Quiero crear un producto' },
  ],
  purchasing: [
    { label: 'Pedido a proveedor', prompt: 'Quiero hacer un pedido a un proveedor' },
    { label: 'Gastos por proveedor', prompt: '¿Cuánto he gastado por proveedor?' },
  ],
  general: [
    { label: 'Resumen del mes', prompt: 'Hazme un resumen de cómo va el negocio este mes' },
    { label: '¿Puedo pagar nóminas?', prompt: '¿Podré pagar las nóminas este mes?' },
    { label: 'Facturas vencidas', prompt: '¿Qué facturas tengo vencidas?' },
  ],
};

/** Lowercase + sin acentos, para comparar contra el texto de la respuesta. */
function normalize(s) {
  return (s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
}

// Frases que indican que la respuesta ya dijo "no hay nada de eso" / "está al día".
// Se evalúan POR FRASE (no en toda la respuesta) para no suprimir un chip por una
// negación que hablaba de otro tema.
const NEGATION_RE = /\b(no hay|no tienes?|no tiene|no existen?|ningun[ao]s?|ningun|sin ninguna?|al dia|nada pendiente|todo cuadrado|sin movimientos|cero|^0\b| 0 )\b/i;

/** Palabras significativas del label de un follow-up (para casarlas con la respuesta). */
function topicWords(followup) {
  return normalize(followup.label).split(/\s+/).filter((w) => w.length > 3);
}

/**
 * Hasta 3 follow-ups para el dominio del turno; cae a `general` si no mapea.
 *
 * F-2 — coherencia con el resultado del turno: si se pasa `responseText`, se
 * suprime un chip cuya temática la respuesta ya negó en la MISMA frase (p.ej. no
 * ofrecer "¿Facturas vencidas?" justo después de "no tienes facturas vencidas").
 * Conservador: solo suprime ante negación localizada; si el filtro vaciara la
 * lista, devuelve la original para no quedarse sin sugerencias.
 */
export function getFollowups(domain, responseText = '') {
  const list = FOLLOWUPS[domain] || FOLLOWUPS.general;
  if (!responseText) return list;

  const negatedSentences = responseText
    .split(/[.!?\n]+/)
    .filter((s) => NEGATION_RE.test(s))
    .map(normalize);
  if (negatedSentences.length === 0) return list;

  const filtered = list.filter((f) => {
    const words = topicWords(f);
    if (words.length === 0) return true;
    const negated = negatedSentences.some((s) => words.every((w) => s.includes(w)));
    return !negated;
  });
  return filtered.length ? filtered : list;
}

export default FOLLOWUPS;
